import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import {
  LayoutDashboard, Package, Users2, ShoppingCart, GitFork,
  Warehouse, UsersRound, Settings, FileBarChart2, Wallet,
  ScrollText, Upload, Building2, LogOut, UserCircle2, Sparkles,
  Truck, MessageSquare, Eye, X, ScanSearch, Download, Store, Database, PackageOpen, TrendingUp, Image as ImageIcon, History, ClipboardList,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePerms } from "@/lib/auth";
import { T } from "@/lib/testIds";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
  { to: "/orders", label: "Orders", icon: ShoppingCart, key: "orders" },
  { to: "/inventory", label: "Inventory", icon: Package, key: "inventory" },
  { to: "/customers", label: "Customers", icon: UserCircle2, key: "customers" },
  { to: "/suggestions", label: "Suggestions", icon: Sparkles, key: "suggestions" },
  { to: "/procurement", label: "Procurement", icon: Truck, key: "procurement" },
  { to: "/inbound", label: "Inbound", icon: PackageOpen, key: "inbound" },
  { to: "/picklist", label: "Picklist", icon: ClipboardList, key: "picklist" },
  { to: "/allocation-history", label: "Allocation History", icon: History, key: "allocation_history" },
  { to: "/price-update", label: "Price Update", icon: TrendingUp, key: "price-update" },
  { to: "/listings", label: "Listings", icon: ScanSearch, key: "listings" },
  { to: "/1688", label: "1688 Sourcing", icon: Store, key: "sourcing1688" },
  { to: "/master", label: "Master Data", icon: Database, key: "master" },
  { to: "/asin-images", label: "ASIN Images", icon: ImageIcon, key: "asin_images" },
  { to: "/catalog", label: "Catalog", icon: PackageOpen, key: "catalog" },
  { to: "/chat", label: "Chat", icon: MessageSquare, key: "chat" },
  { to: "/accounts", label: "Accounts", icon: Building2, key: "accounts" },
  { to: "/mapping", label: "Mapping", icon: GitFork, key: "mapping" },
  { to: "/warehouse", label: "Warehouse", icon: Warehouse, key: "warehouse" },
  { to: "/bank", label: "Bank", icon: Wallet, key: "bank" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, key: "reports" },
  { to: "/imports", label: "Imports", icon: Upload, key: "imports" },
  { to: "/downloads", label: "Downloads", icon: Download, key: "downloads" },
  { to: "/users", label: "Users", icon: Users2, key: "users" },
  { to: "/teams", label: "Teams", icon: UsersRound, key: "teams" },
  { to: "/settings", label: "Settings", icon: Settings, key: "settings" },
  { to: "/logs", label: "Logs", icon: ScrollText, key: "logs" },
];

export default function AppShell({ children }) {
  const { user, logout, refreshPerms, preview, exitPreview } = useAuth();
  const { canView } = usePerms();
  const navigate = useNavigate();
  const location = useLocation();

  // Refresh perms on every route change so team edits propagate without re-login
  useEffect(() => { if (user) refreshPerms(); }, [location.pathname]); // eslint-disable-line

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex flex-col h-full min-h-screen bg-slate-50">
      {preview && (
        <div className="h-9 bg-amber-500 text-slate-900 flex items-center justify-between px-4 shrink-0" data-testid="preview-banner">
          <div className="flex items-center gap-2 text-[12.5px] font-semibold">
            <Eye size={14} />
            Previewing as team <span className="font-mono px-1.5 py-0.5 bg-white/60 border border-amber-700/30 rounded-sm">{preview.team_name}</span>
            <span className="text-amber-900/80 font-normal">— you're seeing the app exactly as this team's members do.</span>
          </div>
          <button
            onClick={exitPreview}
            data-testid="preview-exit-btn"
            className="inline-flex items-center gap-1 h-6 px-2 text-[11.5px] font-semibold bg-slate-900 text-white rounded-sm hover:bg-slate-800"
          >
            <X size={12} /> Exit preview
          </button>
        </div>
      )}
      <div className="flex flex-1 min-h-0">
      <aside className="w-56 shrink-0 border-r border-slate-200 bg-white flex flex-col">
        <div className="h-14 px-4 flex items-center border-b border-slate-200">
          <div className="w-7 h-7 bg-slate-900 rounded-sm flex items-center justify-center mr-2">
            <span className="text-white font-heading font-bold text-[13px]">IR</span>
          </div>
          <span className="font-heading font-semibold tracking-tight text-slate-900">Importrend</span>
        </div>
        <nav className="flex-1 py-2 overflow-y-auto">
          {/* Sep 7 2026 — Dashboard now requires an explicit `dashboard`
              view grant. Admins bypass. Non-admin users without the grant
              lose the sidebar item AND cannot land on `/` (App.js redirects
              them to the first module they can see). Backward-compat maps
              `price-update` → `price_update` since AppShell uses the URL
              slug and Team perms use the module key. */}
          {NAV.filter(({ key }) => {
            const permKey = key === "price-update" ? "price_update" : key;
            return canView(permKey);
          }).map(({ to, label, icon: Icon, key }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={T.sidebarLink(key)}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-1.5 text-[13px] border-l-2 ${
                  isActive
                    ? "border-slate-900 bg-slate-100 text-slate-900 font-medium"
                    : "border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon size={15} strokeWidth={1.75} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-3 flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-[11px] font-semibold text-slate-700">
            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="truncate text-[12px] font-medium text-slate-900">{user?.name || user?.email}</div>
            <div className="truncate text-[10.5px] text-slate-500 uppercase tracking-wider">{user?.role}</div>
          </div>
          <button
            onClick={doLogout}
            data-testid={T.logoutBtn}
            className="text-slate-500 hover:text-slate-900"
            title="Logout"
          >
            <LogOut size={15} />
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 flex flex-col">{children}</main>
      </div>
    </div>
  );
}
